'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Toast } from './Toast.js';
import styles from './ToastProvider.module.css';
const MAX_VISIBLE_TOASTS = 3;
const DEFAULT_DURATION_MS = 4500;
const ToastContext = createContext(null);
/**
 * Toast queue (§3.3 "Snackbar w/ undo", ux.md Toasts): caps ~3 stacked,
 * pause-on-hover, manually dismissable, `aria-live="polite"` region.
 * Consumers use `assetToast(locale, tone, action, assetName)` from
 * `@xdocs/i18n` to build the `ToastInput` before calling `push`.
 */
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const timers = useRef(new Map());
    const dismiss = useCallback((id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
        const timer = timers.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timers.current.delete(id);
        }
    }, []);
    const scheduleDismiss = useCallback((id, durationMs) => {
        const timer = setTimeout(() => dismiss(id), durationMs);
        timers.current.set(id, timer);
    }, [dismiss]);
    const push = useCallback((input) => {
        const id = input.id ?? crypto.randomUUID();
        const durationMs = input.durationMs ?? DEFAULT_DURATION_MS;
        const toast = { ...input, id, durationMs };
        setToasts((prev) => [...prev.slice(-(MAX_VISIBLE_TOASTS - 1)), toast]);
        scheduleDismiss(id, durationMs);
        return id;
    }, [scheduleDismiss]);
    function pause(id) {
        const timer = timers.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timers.current.delete(id);
        }
    }
    function resume(id, durationMs) {
        if (!timers.current.has(id))
            scheduleDismiss(id, durationMs);
    }
    const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);
    return (_jsxs(ToastContext.Provider, { value: value, children: [children, typeof document !== 'undefined' &&
                createPortal(_jsx("div", { className: styles['viewport'], "aria-live": "polite", children: toasts.map((toast) => (_jsx(Toast, { toast: toast, onDismiss: () => dismiss(toast.id), onMouseEnter: () => pause(toast.id), onMouseLeave: () => resume(toast.id, toast.durationMs) }, toast.id))) }), document.body)] }));
}
export function useToast() {
    const context = useContext(ToastContext);
    if (!context)
        throw new Error('useToast must be used within a <ToastProvider>');
    return context;
}
